/**
 * WordCount Component
 * Displays word count and character count statistics
 */

import { HTMLAttributes, forwardRef, useMemo } from 'react'
import { clsx } from 'clsx'
import { FileText, Type, Clock } from 'lucide-react'

export interface WordCountProps extends HTMLAttributes<HTMLDivElement> {
  text: string
  showCharacters?: boolean
  showReadingTime?: boolean
  wordsPerMinute?: number
}

export const WordCount = forwardRef<HTMLDivElement, WordCountProps>(
  (
    {
      text,
      showCharacters = true,
      showReadingTime = true,
      wordsPerMinute = 200,
      className,
      ...props
    },
    ref,
  ) => {
    const stats = useMemo(() => {
      const words = text.trim().split(/\s+/).filter(word => word.length > 0).length
      const characters = text.length
      const readingTime = Math.ceil(words / wordsPerMinute)

      return { words, characters, readingTime }
    }, [text, wordsPerMinute])

    return (
      <div
        ref={ref}
        className={clsx('flex items-center gap-4 text-sm text-gray-600', className)}
        {...props}
      >
        <div className="flex items-center gap-1.5">
          <FileText className="h-4 w-4" />
          <span className="font-medium">{stats.words.toLocaleString()}</span>
          <span>words</span>
        </div>

        {showCharacters && (
          <div className="flex items-center gap-1.5">
            <Type className="h-4 w-4" />
            <span className="font-medium">{stats.characters.toLocaleString()}</span>
            <span>characters</span>
          </div>
        )}

        {showReadingTime && stats.words > 0 && (
          <div className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            <span className="font-medium">{stats.readingTime}</span>
            <span>min read</span>
          </div>
        )}
      </div>
    )
  },
)

WordCount.displayName = 'WordCount'
